import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import formatPrice from '../utils/formatPrice';
import { getAddresses } from '../services/addressService';
import {
  getCheckoutQuote,
  createRazorpayOrder,
  verifyRazorpayPayment,
  placeCodOrder
} from '../services/orderService';
import '../css/checkout.css';
import '../css/checkout-payment.css';
import '../css/phase1.css';
import { trackBeginCheckout } from '../utils/analytics';
import { useToast } from '../contexts/ToastContext';

// Loads the Razorpay Checkout script on demand (only once).
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.getElementById('razorpay-checkout-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-checkout-js';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const formatDeliveryDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};

const CheckoutPage = () => {
  const { items, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [step, setStep] = useState('address'); // 'address' | 'payment'
  const [paymentMethod, setPaymentMethod] = useState('Razorpay'); // 'Razorpay' | 'COD'

  // Only the coupon CODE comes from the cart page — the discount itself is
  // always recalculated by the server.
  const [couponCode, setCouponCode] = useState(location.state?.coupon?.code || '');

  const [couponInput, setCouponInput] = useState(location.state?.coupon?.code || '');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  // Re-price whenever the cart contents change
  const cartSignature = useMemo(
    () => items.map((i) => `${i?.product?._id}:${i?.variant?._id || i?.size}:${i?.qty}`).join('|'),
    [items]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const list = await getAddresses();
        setAddresses(list || []);
        const def = (list || []).find((a) => a.isDefault) || (list || [])[0];
        if (def) setSelectedAddressId(def._id);
      } catch (err) {
        console.error('Failed to load addresses', err);
      } finally {
        setLoadingAddresses(false);
      }
    };
    load();
  }, []);

  const loadQuote = useCallback(async () => {
    if (!items.length) {
      setQuote(null);
      return;
    }
    setQuoteLoading(true);
    setQuoteError('');
    try {
      const q = await getCheckoutQuote({
        addressId: selectedAddressId,
        couponCode: couponCode || undefined,
        paymentMethod
      });
      setQuote(q);
      // COD became unavailable (pincode / order value) — fall back to online
      if (paymentMethod === 'COD' && !q.codAvailable) setPaymentMethod('Razorpay');
    } catch (err) {
      setQuote(null);
      setQuoteError(err.response?.data?.message || 'Could not calculate your total. Please try again.');
    } finally {
      setQuoteLoading(false);
    }
  }, [selectedAddressId, couponCode, paymentMethod, cartSignature]);

  useEffect(() => {
    if (!loadingAddresses) loadQuote();
  }, [loadQuote, loadingAddresses]);

  // analytics: checkout started (once per visit, when the total is known)
  const checkoutTracked = React.useRef(false);
  useEffect(() => {
    if (quote && !checkoutTracked.current && items.length) {
      checkoutTracked.current = true;
      trackBeginCheckout(
        items.map((i) => ({ product: i.product, size: i.size, qty: i.qty, price: i.price ?? i.product?.price })),
        quote.totalPrice
      );
    }
  }, [quote, items]);

  const delivery = quote?.delivery;
  const notServiceable = delivery && delivery.serviceable === false;
  const couponProblem = Boolean(couponCode) && Boolean(quoteError) && /coupon/i.test(quoteError);

  const handleContinueToPayment = () => {
    if (!selectedAddressId) {
      addToast('Please select a shipping address first', 'error');
      return;
    }
    if (notServiceable) return;
    setStep('payment');
  };

  const finishOrder = async (order) => {
    try {
      await clearCart();
    } catch (cartErr) {
      // The server already emptied the cart; this only refreshes the badge
      console.error('Failed to refresh cart after order:', cartErr);
    }
    const oid = order.orderId || order._id;
    navigate(`/order-success/${encodeURIComponent(oid)}`, { state: { order } });
  };

  const handleError = (err, fallback) => {
    const msg = err.response?.data?.message || err.message || fallback;
    addToast(msg, 'error', 5000);
    if (/shipping address/i.test(msg)) {
      navigate('/account', { state: { activeTab: 'address' } });
    } else {
      loadQuote();
    }
  };

  // ---------------- Cash on Delivery ----------------
  const handlePlaceCod = async () => {
    if (placing) return;
    setPlacing(true);
    try {
      const order = await placeCodOrder({ addressId: selectedAddressId, couponCode: couponCode || undefined });
      await finishOrder(order);
    } catch (err) {
      console.error('COD order failed', err);
      handleError(err, 'Could not place your order');
    } finally {
      setPlacing(false);
    }
  };

  // ---------------- Online (Razorpay) ----------------
  const handlePayOnline = async () => {
    if (placing) return;
    setPlacing(true);

    try {
      const sdkReady = await loadRazorpayScript();
      if (!sdkReady) {
        addToast('Unable to load the payment gateway. Please check your internet connection and try again.', 'error', 5000);
        setPlacing(false);
        return;
      }

      const rpOrder = await createRazorpayOrder({
        addressId: selectedAddressId,
        couponCode: couponCode || undefined
      });

      const options = {
        key: rpOrder.keyId,
        amount: rpOrder.amount,
        currency: rpOrder.currency,
        name: 'Mithralaya',
        description: 'Order payment',
        image: `${window.location.origin}/logo192.png`,
        order_id: rpOrder.razorpayOrderId,
        prefill: {
          name: rpOrder.prefill?.name || user?.name || '',
          email: rpOrder.prefill?.email || '',
          contact: rpOrder.prefill?.contact || ''
        },
        theme: { color: '#f4b6c4' },
        modal: {
          ondismiss: () => setPlacing(false)
        },
        handler: async (response) => {
          try {
            const order = await verifyRazorpayPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature
            });
            await finishOrder(order);
          } catch (err) {
            console.error('Payment verification failed', err);
            addToast(
              err.response?.data?.message ||
                `Your payment went through but we could not confirm the order yet. It will appear in My Orders within a few minutes. Payment ID: ${response.razorpay_payment_id}`,
              'error',
              8000
            );
            navigate('/account', { state: { activeTab: 'orders' } });
          } finally {
            setPlacing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        console.error('Razorpay payment failed', resp?.error);
        addToast(`Payment failed: ${resp?.error?.description || 'Please try again.'}`, 'error', 5000);
        setPlacing(false);
      });
      rzp.open();
    } catch (err) {
      console.error('Failed to start payment', err);
      handleError(err, 'Failed to start payment');
      setPlacing(false);
    }
  };

  const canPay = Boolean(quote) && !quoteLoading && !notServiceable && !placing;
  const totalLabel = quote ? formatPrice(quote.totalPrice) : '…';

  const deliveryLine = () => {
    if (!selectedAddressId || !delivery) return null;
    if (notServiceable) {
      return (
        <div className="p1-delivery bad" role="status">
          <strong>Not deliverable.</strong> {delivery.message || 'We do not deliver to this pincode yet. Please choose another address.'}
        </div>
      );
    }
    return (
      <div className="p1-delivery" role="status">
        <span>
          Delivery by <strong>{formatDeliveryDate(delivery.deliveryBy)}</strong>
          {delivery.estimated ? ' (estimated)' : ''}
        </span>
      </div>
    );
  };

  return (
    <div className="checkout-page">
      <h1 className="page-title">Checkout</h1>

      <div className="checkout-progress">
        <div className="checkout-step completed">
          <div className="checkout-circle">✓</div>
          <p>Order Summary</p>
        </div>

        <div className="checkout-line active"></div>

        <div className={`checkout-step ${step === 'address' ? 'active' : 'completed'}`}>
          <div className="checkout-circle">{step === 'payment' ? '✓' : '2'}</div>
          <p>Address</p>
        </div>

        <div className={`checkout-line ${step === 'payment' ? 'active' : ''}`}></div>

        <div className={`checkout-step ${step === 'payment' ? 'active' : ''}`}>
          <div className="checkout-circle">3</div>
          <p>Payment</p>
        </div>
      </div>

      <div className="checkout-layout">
        <div className="checkout-left">
          {step === 'address' && (
            <section className="checkout-addresses">
              <h2>Shipping Address</h2>

              {loadingAddresses ? (
                <div>Loading addresses…</div>
              ) : addresses.length === 0 ? (
                <div>
                  <p>Add a delivery address to continue.</p>
                  <button
                    className="btn-add-address"
                    onClick={() => navigate('/account', { state: { activeTab: 'address' } })}
                  >
                    Add Address +
                  </button>
                </div>
              ) : (
                <div className="address-list">
                  {addresses.map((a) => (
                    <label key={a._id} className={`address-card ${a.isDefault ? 'default' : ''}`}>
                      <input
                        type="radio"
                        name="shippingAddress"
                        checked={selectedAddressId === a._id}
                        onChange={() => setSelectedAddressId(a._id)}
                      />

                      <div className="address-body">
                        <div className="address-top">
                          <strong>{a.fullName}</strong>
                          {a.suggestedName && <span className="address-label">{a.suggestedName}</span>}
                          {a.isDefault && <span className="address-default">Default</span>}
                        </div>

                        <div className="address-line">{a.address}</div>
                        {a.locality && <div className="address-line">{a.locality}</div>}
                        <div className="address-meta">{a.city} - {a.pincode}</div>
                        <div className="address-meta">{a.state}</div>
                        <div className="address-meta">Phone: {a.phone}</div>
                      </div>
                    </label>
                  ))}

                  {deliveryLine()}

                  <div style={{ marginTop: 12 }}>
                    <button
                      className="addeditcheckaddress"
                      onClick={() => navigate('/account', { state: { activeTab: 'address' } })}
                    >
                      Add or Edit Addresses
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {step === 'payment' && (
            <section className="checkout-payment">
              <h2>Payment</h2>

              {deliveryLine()}

              <div className="p1-pay-options" role="radiogroup" aria-label="Payment method">
                <label className={`p1-pay-option ${paymentMethod === 'Razorpay' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    checked={paymentMethod === 'Razorpay'}
                    onChange={() => setPaymentMethod('Razorpay')}
                  />
                  <span>
                    <strong>Pay online</strong>
                    <small>UPI, cards, netbanking, wallets and EMI</small>
                  </span>
                </label>

                <label
                  className={`p1-pay-option ${paymentMethod === 'COD' ? 'selected' : ''} ${
                    quote && !quote.codAvailable ? 'disabled' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    checked={paymentMethod === 'COD'}
                    disabled={!quote || !quote.codAvailable}
                    onChange={() => setPaymentMethod('COD')}
                  />
                  <span>
                    <strong>Cash on Delivery</strong>
                    {quote && !quote.codAvailable ? (
                      <small className="p1-reason">{quote.codUnavailableReason}</small>
                    ) : (
                      <small>
                        Pay by cash or UPI when the parcel arrives
                        {quote?.codFeeAmount > 0 ? ` · ${formatPrice(quote.codFeeAmount)} handling fee` : ''}
                      </small>
                    )}
                  </span>
                </label>
              </div>

              {paymentMethod === 'Razorpay' && (
                <div className="pay-online-card">
                  <div className="pay-online-head">
                    <div className="pay-online-title">
                      <span className="pay-online-badge">100% Secure</span>
                      <strong>Pay Online</strong>
                      <p>Choose from UPI, Cards, Netbanking, Wallets &amp; EMI in the next step</p>
                    </div>
                  </div>

                  <div className="pay-methods-grid">
                    <div className="pay-method">
                      <span className="pay-method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M7 3l5 9-5 9" /><path d="M13 3l5 9-5 9" />
                        </svg>
                      </span>
                      <div>
                        <strong>UPI</strong>
                        <small>GPay, PhonePe, Paytm &amp; more</small>
                      </div>
                    </div>

                    <div className="pay-method">
                      <span className="pay-method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
                        </svg>
                      </span>
                      <div>
                        <strong>Cards</strong>
                        <small>Credit &amp; Debit — Visa, Mastercard, RuPay</small>
                      </div>
                    </div>

                    <div className="pay-method">
                      <span className="pay-method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 21h18" /><path d="M4 10h16" /><path d="M12 3L4 10h16L12 3z" /><path d="M6 10v8M10 10v8M14 10v8M18 10v8" />
                        </svg>
                      </span>
                      <div>
                        <strong>Netbanking</strong>
                        <small>All major Indian banks</small>
                      </div>
                    </div>

                    <div className="pay-method">
                      <span className="pay-method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="6" width="20" height="13" rx="2" /><path d="M16 12.5h2" /><path d="M2 9h20" opacity="0" /><path d="M6 6V5a2 2 0 012-2h8a2 2 0 012 2v1" />
                        </svg>
                      </span>
                      <div>
                        <strong>Wallets &amp; EMI</strong>
                        <small>Mobikwik, Freecharge &amp; EMI options</small>
                      </div>
                    </div>
                  </div>

                  <div className="pay-online-foot">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" />
                    </svg>
                    <span>Payments are encrypted and processed securely by Razorpay.</span>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="checkout-right">
          <h2>Order Summary</h2>

          {quoteError && (
            <div className="p1-error" role="alert">
              {quoteError}
              {couponProblem && (
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="p1-link-btn" onClick={() => { setCouponCode(''); setCouponInput(''); }}>
                    Continue without the coupon
                  </button>
                </div>
              )}
              {!couponProblem && /cart|stock|available/i.test(quoteError) && (
                <div style={{ marginTop: 8 }}>
                  <Link to="/cart" className="p1-link-btn">Go back to cart</Link>
                </div>
              )}
            </div>
          )}

          <div className="summary-row">
            <span>Items</span>
            <span>{quote ? quote.itemCount : items.reduce((s, it) => s + Number(it.qty || 0), 0)}</span>
          </div>

          <div className="summary-row">
            <span>Subtotal</span>
            <span>{quote ? formatPrice(quote.itemsPrice) : '…'}</span>
          </div>

          {/* Coupon — can be added or changed here too (validated by the server) */}
          <form
            className="m-coupon-form"
            onSubmit={(e) => {
              e.preventDefault();
              setCouponCode(couponInput.trim().toUpperCase());
            }}
          >
            <label htmlFor="checkout-coupon">Coupon code</label>
            <div className="m-coupon-row">
              <input
                id="checkout-coupon"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
                placeholder="Enter code"
                autoComplete="off"
              />
              {couponCode && quote?.coupon ? (
                <button type="button" onClick={() => { setCouponCode(''); setCouponInput(''); }}>Remove</button>
              ) : (
                <button type="submit" disabled={!couponInput.trim()}>Apply</button>
              )}
            </div>
            {couponCode && quote?.coupon && (
              <p className="m-coupon-ok">{quote.coupon.code} applied — you save {formatPrice(quote.discount)}</p>
            )}
          </form>

          {quote?.discount > 0 && (
            <div className="summary-row">
              <span>Coupon {quote.coupon?.code ? `(${quote.coupon.code})` : ''}</span>
              <span style={{ color: 'green' }}>- {formatPrice(quote.discount)}</span>
            </div>
          )}

          <div className="summary-row">
            <span>Shipping</span>
            <span>{quote ? (quote.shippingPrice ? formatPrice(quote.shippingPrice) : 'Free') : '…'}</span>
          </div>

          {quote?.codFee > 0 && (
            <div className="summary-row">
              <span>Cash on Delivery fee</span>
              <span>{formatPrice(quote.codFee)}</span>
            </div>
          )}

          {quote && !quote.taxIncluded && (
            <div className="summary-row">
              <span>GST</span>
              <span>{formatPrice(quote.taxPrice)}</span>
            </div>
          )}

          <hr />

          <div className="summary-row total">
            <span>Grand Total</span>
            <span>{quoteLoading ? '…' : totalLabel}</span>
          </div>
          {quote?.taxIncluded && (
            <p className="p1-summary-note">Includes {formatPrice(quote.taxPrice)} GST</p>
          )}

          {step === 'address' && (
            <button
              className="btn-continue-payment"
              onClick={handleContinueToPayment}
              disabled={!selectedAddressId || notServiceable || Boolean(quoteError)}
            >
              Continue to Payment
            </button>
          )}

          {step === 'payment' && (
            <>
              <button
                className="btn-back-to-address"
                onClick={() => setStep('address')}
                style={{ marginBottom: 10 }}
                disabled={placing}
              >
                Back to Address
              </button>
              <button
                className="btn-place-order wide pay-razorpay-btn"
                onClick={paymentMethod === 'COD' ? handlePlaceCod : handlePayOnline}
                disabled={!canPay}
              >
                {placing ? (
                  <span className="pay-btn-inner">
                    <span className="pay-btn-spinner" aria-hidden="true"></span>
                    {paymentMethod === 'COD' ? 'Placing order…' : 'Processing payment…'}
                  </span>
                ) : paymentMethod === 'COD' ? (
                  <span className="pay-btn-inner">Place order · {totalLabel}</span>
                ) : (
                  <span className="pay-btn-inner">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" />
                    </svg>
                    Pay {totalLabel} securely
                  </span>
                )}
              </button>
              {paymentMethod === 'Razorpay' && (
                <p className="pay-secure-note">
                  <span className="pay-secure-dot"></span>
                  Secured by Razorpay
                </p>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CheckoutPage;
